// @flow
import { PDFFillerContext } from 'pages/Contexts/PdfFillerContext'
import * as React from 'react'
import styled from 'styled-components'
import type { FontSize, InputField, InputType } from 'types/input-field'
import type { PdfPage as PdfPageType } from 'types/pdf-page'
import * as uuid from 'uuid'

type Props = {
  page: PdfPageType,
}

const Overlay = styled.div`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`

function PdfPageOverlay({ page }: Props): React.Node {
  const ref = React.useRef(null)

  const {
    lastAppliedSettings,
    overlayActive,
    resourceId,
    setInputFields,
    setSelectedInputFieldId,
    zoom,
  } = React.useContext(PDFFillerContext)

  const getCoordinates = React.useCallback((event) => {
    if (!ref.current) return { x: 0, y: 0 }

    const rect = ref.current.getBoundingClientRect()

    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    return { x, y }
  }, [])

  const getInputSettings = React.useCallback(
    (
      type: InputType,
      pageWidth: number,
      pageHeight: number
    ): { font_size: FontSize, width: number, height: number } => {
      if (lastAppliedSettings[type]) {
        return {
          font_size: lastAppliedSettings[type].font_size,
          width: lastAppliedSettings[type].width,
          height: lastAppliedSettings[type].height,
        }
      }

      switch (type) {
        case 'checkbox':
          return {
            font_size: 'auto',
            width: (4 * pageWidth) / 100,
            height: (4 * pageWidth) / 100,
          }
        case 'signature':
        case 'initials':
          return {
            font_size: 'auto',
            width: (25 * pageWidth) / 100,
            height: (4 * pageHeight) / 100,
          }
        default:
          return {
            font_size: '28',
            width: (15 * pageWidth) / 100,
            height: (3 * pageHeight) / 100,
          }
      }
    },
    [lastAppliedSettings]
  )

  const onClick = React.useCallback(
    (event) => {
      if (!overlayActive) return null

      const { x, y } = getCoordinates(event)

      const inputSettings = getInputSettings(
        overlayActive,
        page.width || 1,
        page.height || 1
      )

      const newInputField: InputField = {
        id: uuid.v4(),
        is_office_use: false,
        isResizing: false,
        left: x / zoom,
        file_page: page.id,
        font_size: inputSettings.font_size,
        pdf_document: resourceId,
        required: false,
        top: y / zoom,
        value: '',
        input_type: overlayActive,
        width: inputSettings.width,
        height: inputSettings.height,
        client_signature: null,
      }

      setInputFields((prev) => ({
        ...prev,
        [newInputField.id]: newInputField,
      }))

      setSelectedInputFieldId(newInputField.id)
    },
    [
      getCoordinates,
      getInputSettings,
      overlayActive,
      page.height,
      page.id,
      page.width,
      resourceId,
      setInputFields,
      setSelectedInputFieldId,
      zoom,
    ]
  )

  if (!overlayActive) return null

  return <Overlay onClick={onClick} ref={ref} />
}

export default PdfPageOverlay
